// src/services/cryptoWS.js
// -----------------------------------------
// Binance HTTP + WebSocket helpers
// -----------------------------------------

const BINANCE_BASE = "https://api.binance.com/api/v3";

/**
 * Fetch top N assets from Binance, using 24h ticker stats.
 *
 * Normalized shape:
 * {
 *   id,
 *   rank,
 *   symbol,
 *   name,
 *   priceUsd,
 *   changePercent24Hr,
 * }
 */
export async function fetchTopAssets(limit = 50) {
  const res = await fetch(`${BINANCE_BASE}/ticker/24hr`);

  if (!res.ok) {
    throw new Error(`Binance error ${res.status}`);
  }

  const data = await res.json();

  // Filter to USDT spot pairs, drop leveraged/ETF style tokens
  const usdt = data.filter((t) => {
    const s = t.symbol || "";
    return (
      s.endsWith("USDT") &&
      !s.endsWith("UPUSDT") &&
      !s.endsWith("DOWNUSDT") &&
      !s.includes("BULL") &&
      !s.includes("BEAR")
    );
  });

  // Sort by quoteVolume (most traded first)
  usdt.sort(
    (a, b) =>
      parseFloat(b.quoteVolume || "0") - parseFloat(a.quoteVolume || "0")
  );

  // Take top N and normalize
  return usdt.slice(0, limit).map((t, idx) => ({
    id: t.symbol,
    rank: idx + 1,
    symbol: t.symbol,
    name: t.symbol, // REST doesn't give pretty name
    priceUsd: parseFloat(t.lastPrice || "0"),
    changePercent24Hr: parseFloat(t.priceChangePercent || "0"),
  }));
}

/**
 * Optional: single-symbol Binance trade WebSocket.
 * Not used in MarketsRealtime below, but your useBinanceTicker hook depends on it.
 */
export function binanceSocket(symbol, onTrade) {
  if (!symbol) {
    throw new Error("binanceSocket requires a symbol, e.g. 'BTCUSDT'");
  }

  const stream = symbol.toLowerCase() + "@trade";
  const url = `wss://stream.binance.com:9443/ws/${stream}`;
  const ws = new WebSocket(url);

  ws.onopen = () => {
    console.log("[Binance WS] connected:", symbol);
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (onTrade) onTrade(data);
    } catch (e) {
      console.error("[Binance WS] parse error", e);
    }
  };

  ws.onerror = (err) => {
    console.error("[Binance WS] error", err);
  };

  ws.onclose = () => {
    console.log("[Binance WS] closed:", symbol);
  };

  return ws;
}

/**
 * Subscribes to the Binance WebSocket stream for all ticker updates.
 * Provides a normalized list of tickers to the onTickers callback.
 *
 * @param {function} onTickers - Callback to receive the normalized ticker data.
 */
export function binanceTickersSocket(onTickers) {
  const url = `wss://stream.binance.com:9443/ws/!ticker@arr`;
  const ws = new WebSocket(url);

  ws.onopen = () => {
    console.log("[Binance WS] connected: all tickers");
  };

  ws.onmessage = (event) => {
    try {
      const tickers = JSON.parse(event.data);
      if (onTickers) {
        // Normalize the data to match the shape of the REST API
        const normalized = tickers.map((t) => ({
          id: t.s,
          symbol: t.s,
          priceUsd: parseFloat(t.c),
          changePercent24Hr: parseFloat(t.P),
          volumeUsd24Hr: parseFloat(t.q),
        }));
        onTickers(normalized);
      }
    } catch (e) {
      console.error("[Binance WS] parse error", e);
    }
  };

  ws.onerror = (err) => {
    console.error("[Binance WS] error", err);
  };

  ws.onclose = () => {
    console.log("[Binance WS] closed: all tickers");
  };

  return ws;
}

/**
 * Fetches historical k-line (candlestick) data for a given symbol.
 * @param {string} symbol - The asset symbol (e.g., 'BTCUSDT').
 * @param {string} interval - The time interval (e.g., '1d', '4h', '1h').
 * @returns {Array} - A normalized array of historical data points.
 */
export async function fetchAssetHistory(symbol, interval = '1d') {
  const url = `${BINANCE_BASE}/klines?symbol=${symbol}&interval=${interval}&limit=100`;
  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Binance error ${res.status}`);
  }

  const data = await res.json();

  // Normalize the k-line data
  return data.map(kline => ({
    time: kline[0],
    open: parseFloat(kline[1]),
    high: parseFloat(kline[2]),
    low: parseFloat(kline[3]),
    close: parseFloat(kline[4]),
    volume: parseFloat(kline[5]),
  }));
}
